import { reportError } from "@lib/utils/reportError";
import localForage from "localforage";

const STORE_NAME = "trackshare-cache";
const STORAGE_VERSION = "v1";

export class Storage {
  private store: LocalForage | null;

  constructor(driver: string | string[] = [localForage.INDEXEDDB, localForage.WEBSQL, localForage.LOCALSTORAGE]) {
    try {
      // Convert to array if single driver
      const drivers = Array.isArray(driver) ? driver : [driver];

      // Check if ANY of the requested drivers are supported
      const hasSupport = drivers.some(d => localForage.supports(d));

      if (!hasSupport) {
        if (typeof window !== 'undefined') {
          // No storage available - expected for bots/agents, don't log if server-side
          console.warn('Storage unavailable - operating without cache');
        }
        this.store = null;
        return;
      }

      // At least one driver is supported - create instance normally
      this.store = localForage.createInstance({
        driver: driver,
        name: STORE_NAME,
      });
    } catch (error) {
      reportError(error);
      this.store = null;
    }
  }

  /**
   * Prefix a key with the storage version.
   * Format: v1/<key>
   */
  private prefixKey(key: string): string {
    return `${STORAGE_VERSION}/${key}`;
  }

  async getItem<T>(key: string, deprecatedKey?: string): Promise<T | null> {
    if (!this.store) return null;

    const prefixedKey = this.prefixKey(key);

    try {
      const value = await this.store.getItem<T>(prefixedKey);

      if (value !== null) {
        return value;
      } else if (value === null && deprecatedKey !== undefined) {
        // Try the deprecated key (also prefixed for consistency)
        const prefixedDeprecatedKey = this.prefixKey(deprecatedKey);
        const valueToMigrate = await this.store.getItem<T>(prefixedDeprecatedKey);
        if (valueToMigrate !== null) {
          await this.store.setItem(prefixedKey, valueToMigrate)
          await this.store.removeItem(prefixedDeprecatedKey)

          return valueToMigrate
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error) {
      reportError(error);
      return null;
    }
  }

  async setItem<T>(key: string, item: T) {
    if (!this.store) return;

    const prefixedKey = this.prefixKey(key);

    try {
      return await this.store.setItem<T>(prefixedKey, item);
    } catch (error) {
      reportError(error);
    }
  }

  async removeItem(key: string) {
    if (!this.store) return;

    const prefixedKey = this.prefixKey(key);

    try {
      return await this.store.removeItem(prefixedKey);
    } catch (error) {
      reportError(error);
    }
  }

  async clear() {
    if (!this.store) return;

    try {
      return await this.store.clear();
    } catch (error) {
      reportError(error);
    }
  }

  async keys(): Promise<string[]> {
    if (!this.store) return [];

    try {
      return await this.store.keys();
    } catch (error) {
      reportError(error);
      return [];
    }
  }
}
